import { ChevronRight } from "lucide-react";
import { cn } from "lib/utils";
import type { DueList } from "../api/OutreachTouchesApi";
import { PIPELINE_STAGES, SIDE_STAGES, stageCount, stageTouches, type Stage, type StageKey } from "../dueStages";

/** A record just moved into a stage (n tells two moves into the same stage apart, so the animation replays). */
export interface Arrival { stage: StageKey; n: number }

interface Props {
  data: DueList;
  selected: StageKey;
  onSelect: (key: StageKey) => void;
  arrival?: Arrival | null;
}

function stageNumbers(stage: Stage, data: DueList) {
  if (stage.key === "warm") return { count: stageCount(stage, data), overdue: 0, waiting: 0, quiet: 0 };
  const rows = stageTouches(stage, data);
  return { count: stageCount(stage, data), overdue: rows.filter((t) => t.overdue_days > 0).length, waiting: 0, quiet: 0 };
}

/** The "+1" that floats up from a tile when a record lands in it. */
function ArrivalBadge({ n }: { n: number }) {
  return (
    <span key={n} className="due-float-up absolute -top-3 right-2 z-10 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground pointer-events-none" aria-hidden>
      +1
    </span>
  );
}

/** What needs a look in a stage, as words: shown on hover and read out by screen readers; the tab itself only shows a dot. */
function warnings(overdue: number, waiting: number, quiet: number): string[] {
  return [overdue > 0 && `${overdue} overdue`, waiting > 0 && `${waiting} waiting on us`, quiet > 0 && `${quiet} no answer`].filter(Boolean) as string[];
}

/** One tab: the stage name, its count as a small badge and, when something needs a look, an amber dot. The open tab is underlined
 *  (main row) or tinted (the smaller "also" row). */
function StageTab({ stage, data, active, onSelect, arrival, side }: { stage: Stage; data: DueList; active: boolean; onSelect: () => void; arrival: Arrival | null; side?: boolean }) {
  const { count, overdue, waiting, quiet } = stageNumbers(stage, data);
  const flags = warnings(overdue, waiting, quiet);
  return (
    <div className="relative shrink-0">
      {arrival && <ArrivalBadge n={arrival.n} />}
      {/* The pulse is a separate keyed overlay, so the tab itself never remounts (a focused tab keeps focus). */}
      {arrival && <span key={arrival.n} aria-hidden className={cn("due-tile-pulse pointer-events-none absolute inset-x-0", side ? "inset-y-0 rounded-md" : "top-1 bottom-1 rounded-lg")} />}
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onSelect}
        title={flags.length ? flags.join(", ") : undefined}
        className={cn(
          "inline-flex items-center gap-1.5 whitespace-nowrap font-medium transition-colors",
          side
            ? cn("rounded-md px-2.5 py-1 text-xs", active ? "bg-primary-soft text-primary" : "text-muted-foreground hover:text-foreground hover:bg-surface-muted")
            : cn("border-b-2 -mb-px px-2.5 py-2.5 text-sm", active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"),
        )}
      >
        {stage.tab}
        <span className={cn(
          "rounded-full px-1.5 py-px text-xs font-semibold tabular-nums",
          active ? "bg-primary-soft text-primary" : count === 0 ? "bg-surface-muted text-muted-foreground/70" : "bg-surface-muted text-foreground",
          side && active && "bg-surface",
          arrival && "due-count-bump",
        )}>{count}</span>
        {flags.length > 0 && (
          <>
            <span aria-hidden className="w-2 h-2 rounded-full bg-warning shrink-0" />
            <span className="sr-only">{flags.join(", ")}</span>
          </>
        )}
      </button>
    </div>
  );
}

/** The pipeline as one row of tabs (chevrons show the order; tabs wrap rather than scroll on a narrow screen), with the two
 *  side stages as a smaller row beneath it. */
export default function DueStageStrip({ data, selected, onSelect, arrival }: Props) {
  const arrivalFor = (key: StageKey) => (arrival?.stage === key ? arrival : null);
  return (
    <div role="tablist" aria-label="Stage" className="space-y-2">
      <div className="flex flex-wrap items-end border-b border-border">
        {PIPELINE_STAGES.map((stage, i) => (
          <div key={stage.key} className="flex items-center shrink-0">
            <StageTab stage={stage} data={data} active={selected === stage.key} onSelect={() => onSelect(stage.key)} arrival={arrivalFor(stage.key)} />
            {i < PIPELINE_STAGES.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0 mb-0.5" aria-hidden />}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-xs text-muted-foreground pr-1" aria-hidden>Also:</span>
        {SIDE_STAGES.map((stage) => (
          <StageTab key={stage.key} stage={stage} data={data} active={selected === stage.key} onSelect={() => onSelect(stage.key)} arrival={arrivalFor(stage.key)} side />
        ))}
      </div>
    </div>
  );
}
